/**
 * `/lotteries/:id` overview with tabs: Overview, Config versions (JsonDiff), Entries, Draw, Winners, Audit.
 * Entries / Winners tabs are skeletons the entries, outcome and winners agents replace through the registry.
 */
import { useQuery } from '@tanstack/react-query';
import { validateLotteryConfig, type LotteryAdminDto } from '@windrop/contracts';
import { Button, Skeleton, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { JsonDiff } from '../../components/JsonDiff';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline, type TimelineEvent } from '../../components/Timeline';
import { auditApi, lotteriesApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { readRuntimeConfig } from '../../lib/runtime-config';
import { LifecycleActions } from './lifecycle';
import {
  MetricsPanel,
  PendingVersionActions,
  PlatformsEditor,
  PublishGates,
  ShareLinksPanel,
} from './LotteryOperations';
import { lotteriesOpsApi } from '../../lib/api/admin';

export const LOTTERY_TABS = [
  { id: '', label: 'Overview' },
  { id: 'versions', label: 'Config versions' },
  { id: 'entries', label: 'Entries' },
  { id: 'draw', label: 'Draw' },
  { id: 'winners', label: 'Winners' },
  { id: 'audit', label: 'Audit' },
] as const;

export interface LotteryOutletContext {
  lottery: LotteryAdminDto;
}

export const useLottery = (): LotteryOutletContext => useOutletContext<LotteryOutletContext>();

export function LotteryDetailPage() {
  const { id = '' } = useParams();
  const query = useQuery({
    queryKey: ['lottery', id],
    queryFn: () => lotteriesApi.get(id),
    enabled: Boolean(id),
  });
  const { webOrigin } = readRuntimeConfig();

  if (query.isPending) return <Skeleton shape="block" height={240} label="Loading lottery" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Lottery not found"
        body="It may belong to another tenant or have been removed."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button href="/lotteries">Back to lotteries</Button>}
      />
    );
  }
  const lottery = query.data;
  const title =
    lottery.config.presentation.content[lottery.config.identity.locales[0] ?? 'en']?.title ?? lottery.slug;

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Lotteries', to: '/lotteries' }, { label: title }]}
        title={title}
        meta={<StatusBadge kind="LotteryStatus" value={lottery.status} />}
        description={`${humanizeStatus(lottery.mechanic)} · ${lottery.slug} · v${lottery.configVersion}${lottery.publishedConfigVersion ? ` (published v${lottery.publishedConfigVersion})` : ''}`}
        actions={
          <>
            <Button
              variant="secondary"
              href={`${webOrigin}/l/${lottery.slug}${lottery.publishedConfigVersion === lottery.configVersion ? '' : `?preview=${lottery.configVersion}`}`}
              target="_blank"
              rel="noreferrer"
            >
              {lottery.publishedConfigVersion === lottery.configVersion
                ? 'Open participant page'
                : 'Preview as participant'}
            </Button>
            {['DRAFT', 'REVIEW', 'APPROVED'].includes(lottery.status) ? (
              <Can permission="lottery.update">
                <Button variant="secondary" href={`/lotteries/${lottery.lotteryId}/edit`}>
                  Edit in wizard
                </Button>
              </Can>
            ) : null}
            <LifecycleActions lotteryId={lottery.lotteryId} slug={lottery.slug} status={lottery.status} />
          </>
        }
      />
      <ul className="wdc-tabs" role="list">
        {LOTTERY_TABS.map((t) => (
          <li key={t.id}>
            <NavLink to={`/lotteries/${lottery.lotteryId}${t.id ? `/${t.id}` : ''}`} end>
              {t.label}
            </NavLink>
          </li>
        ))}
      </ul>
      <Outlet context={{ lottery } satisfies LotteryOutletContext} />
    </>
  );
}

export function LotteryOverviewTab() {
  const { lottery } = useLottery();
  const intl = useIntl();
  const validation = useMemo(
    () => validateLotteryConfig(lottery.config, { paymentProviderConfigured: true }),
    [lottery.config],
  );
  const cfg = lottery.config;
  return (
    <div className="wdc-grid">
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
        <h2>Schedule</h2>
        <dl className="wdc-facts">
          <div>
            <dt>Opens</dt>
            <dd>{intl.formatDateTime(cfg.schedule.opensAt)}</dd>
          </div>
          <div>
            <dt>Closes</dt>
            <dd>{intl.formatDateTime(cfg.schedule.closesAt)}</dd>
          </div>
          <div>
            <dt>Draw</dt>
            <dd>
              {cfg.schedule.drawAt
                ? intl.formatDateTime(cfg.schedule.drawAt)
                : 'At confirmed entry (instant mechanic)'}
            </dd>
          </div>
          <div>
            <dt>Timezone</dt>
            <dd>{cfg.identity.timezone}</dd>
          </div>
        </dl>
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
        <h2>Entry</h2>
        <dl className="wdc-facts">
          <div>
            <dt>Mode</dt>
            <dd>
              {cfg.entry.mode === 'PAID'
                ? `Paid · ${intl.formatMoney(cfg.entry.priceMinor, cfg.identity.currency)}`
                : 'Free'}
            </dd>
          </div>
          <div>
            <dt>Entries so far</dt>
            <dd>{lottery.hasEntries ? 'Yes — material changes create a new version' : 'None yet'}</dd>
          </div>
          <div>
            <dt>Prizes</dt>
            <dd>{cfg.prizes.prizes.length}</dd>
          </div>
          <div>
            <dt>Liability</dt>
            <dd>{intl.formatMoney(lottery.validation.liabilityMinor, cfg.identity.currency)}</dd>
          </div>
        </dl>
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
        <h2>Validation</h2>
        {validation.ok && lottery.validation.ok ? (
          <ToneBadge tone="success">Publishable — no blocking issues</ToneBadge>
        ) : (
          <ul>
            {[
              ...lottery.validation.issues,
              ...validation.issues.filter(
                (i) => !lottery.validation.issues.some((s) => s.path === i.path && s.code === i.code),
              ),
            ].map((i) => (
              <li key={`${i.path}-${i.code}`}>
                <ToneBadge tone={i.severity === 'error' ? 'error' : 'warning'}>{i.severity}</ToneBadge>{' '}
                {i.message} <code>{i.path}</code>
              </li>
            ))}
          </ul>
        )}
        <p className="wd-muted" style={{ marginTop: 'var(--wd-space-2)' }}>
          Server validation is authoritative; the client preview uses the same rules.
        </p>
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
        <h2>Fairness</h2>
        <dl className="wdc-facts">
          <div>
            <dt>Seed commitment</dt>
            <dd>
              {lottery.seedCommit ? <code>{lottery.seedCommit.slice(0, 16)}…</code> : 'Committed at publish'}
            </dd>
          </div>
          <div>
            <dt>Algorithm</dt>
            <dd>
              <code>{cfg.outcome.algorithmVersion}</code>
            </dd>
          </div>
        </dl>
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)', gridColumn: '1 / -1' }}>
        <h2>Live numbers</h2>
        <MetricsPanel lotteryId={lottery.lotteryId} />
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)', gridColumn: '1 / -1' }}>
        <h2>Publish gates (§5.4)</h2>
        <PublishGates lotteryId={lottery.lotteryId} />
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)', gridColumn: '1 / -1' }}>
        <h2>Share links and QR (§12.1)</h2>
        <ShareLinksPanel lotteryId={lottery.lotteryId} />
      </section>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)', gridColumn: '1 / -1' }}>
        <h2>Platforms (D06)</h2>
        <PlatformsEditor lotteryId={lottery.lotteryId} status={lottery.status} />
      </section>
    </div>
  );
}

export function LotteryVersionsTab() {
  const { lottery } = useLottery();
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['lottery', lottery.lotteryId, 'versions'],
    queryFn: () => lotteriesOpsApi.versions(lottery.lotteryId),
  });
  const versions = query.data?.items ?? [];
  const [left, setLeft] = useState<number | null>(null);
  const [right, setRight] = useState<number | null>(null);
  const sorted = [...versions].sort((a, b) => a.configVersion - b.configVersion);
  const l = sorted.find((v) => v.configVersion === (left ?? sorted[sorted.length - 2]?.configVersion));
  const r = sorted.find((v) => v.configVersion === (right ?? sorted[sorted.length - 1]?.configVersion));
  if (query.isPending) return <Skeleton shape="block" height={160} label="Loading versions" />;
  if (query.isError || sorted.length === 0) {
    return (
      <EmptyState
        title="No version history yet"
        body={`Versions appear here once the lotteries module records them; the current configuration is v${lottery.configVersion}.`}
        action={<Button href={`/lotteries/${lottery.lotteryId}`}>Back to overview</Button>}
      />
    );
  }
  const options = sorted.map((v) => ({
    value: String(v.configVersion),
    label: `v${v.configVersion} · ${intl.formatDateTime(v.createdAt)}${v.published ? ' · published' : ''}`,
  }));
  return (
    <div className="wd-stack">
      <PendingVersionActions lottery={lottery} versions={versions} />
      <ul className="wd-muted" data-testid="version-list">
        {sorted.map((v) => (
          <li key={v.configVersion}>
            v{v.configVersion} ·{' '}
            {v.published ? `published ${intl.formatDateTime(v.publishedAt ?? v.createdAt)}` : 'pending'}
            {v.supersededAt ? ` · superseded ${intl.formatDateTime(v.supersededAt)}` : ''}
            {v.approvedBy ? ' · approved' : ''} · <code>{v.configHash.slice(0, 12)}</code>
          </li>
        ))}
      </ul>
      <div className="wdc-form-grid">
        <label className="wd-field">
          <span className="wd-field__label">Compare from</span>
          <select
            className="wd-select"
            value={l?.configVersion ?? ''}
            onChange={(e) => setLeft(Number(e.target.value))}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="wd-field">
          <span className="wd-field__label">to</span>
          <select
            className="wd-select"
            value={r?.configVersion ?? ''}
            onChange={(e) => setRight(Number(e.target.value))}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {r?.changeNote ? (
        <div className="wdc-callout">
          <p>
            <strong>Change note:</strong> {r.changeNote}
          </p>
          {r.participantRemedy ? <p>Participant remedy: {r.participantRemedy}</p> : null}
        </div>
      ) : null}
      <JsonDiff
        before={l?.config ?? {}}
        after={r?.config ?? {}}
        beforeLabel={l ? `v${l.configVersion}` : 'Nothing'}
        afterLabel={r ? `v${r.configVersion}` : 'Nothing'}
      />
    </div>
  );
}

export function LotteryDrawTab() {
  const { lottery } = useLottery();
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['lottery', lottery.lotteryId, 'precheck'],
    queryFn: () => lotteriesApi.precheck(lottery.lotteryId),
    retry: false,
  });
  if (query.isPending) return <Skeleton shape="block" height={160} label="Running pre-draw checks" />;
  if (query.isError) {
    return (
      <EmptyState
        title="Pre-draw checks are not available"
        body="The draw control room (outcome module) publishes the §9.5 gates here once it ships."
        action={<Button href="/draws">Open draw control room</Button>}
      />
    );
  }
  const p = query.data;
  return (
    <div className="wd-stack">
      <p>
        {p.ready ? (
          <ToneBadge tone="success">Ready to draw</ToneBadge>
        ) : (
          <ToneBadge tone="warning">Not ready</ToneBadge>
        )}{' '}
        <span className="wd-muted">checked {intl.formatRelative(p.checkedAt)}</span>
      </p>
      <table className="wdc-table">
        <caption className="wd-sr-only">Pre-draw gates</caption>
        <thead>
          <tr>
            <th scope="col">Gate</th>
            <th scope="col">Result</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {p.gates.map((g) => (
            <tr key={g.gate}>
              <th scope="row">{humanizeStatus(g.gate)}</th>
              <td>
                {g.ok ? (
                  <ToneBadge tone="success">Pass</ToneBadge>
                ) : (
                  <ToneBadge tone="error">Blocked</ToneBadge>
                )}
              </td>
              <td>{g.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="wdc-facts">
        <div>
          <dt>Eligible entries</dt>
          <dd className="wd-tabular">{intl.formatNumber(p.counts.eligible)}</dd>
        </div>
        <div>
          <dt>Pending payment</dt>
          <dd className="wd-tabular">{intl.formatNumber(p.counts.pending)}</dd>
        </div>
        <div>
          <dt>Refunded</dt>
          <dd className="wd-tabular">{intl.formatNumber(p.counts.refunded)}</dd>
        </div>
        <div>
          <dt>Disqualified</dt>
          <dd className="wd-tabular">{intl.formatNumber(p.counts.disqualified)}</dd>
        </div>
      </dl>
      <p>
        <Link to="/draws" className="wd-btn" data-variant="secondary">
          Open draw control room
        </Link>
      </p>
    </div>
  );
}

export function LotteryAuditTab() {
  const { lottery } = useLottery();
  const query = useQuery({
    queryKey: ['audit', 'lottery', lottery.lotteryId],
    queryFn: () => auditApi.list({ objectType: 'lottery', objectId: lottery.lotteryId }),
    retry: false,
  });
  if (query.isPending) return <Skeleton shape="block" height={160} label="Loading audit trail" />;
  if (query.isError) {
    return (
      <EmptyState
        title="Audit trail not available"
        body="The audit endpoint answers here once the analytics module ships it."
        action={<Button href="/audit">Open audit & incidents</Button>}
      />
    );
  }
  const events: TimelineEvent[] = query.data.items.map((e) => {
    const details = e.details as { before?: unknown; after?: unknown };
    return {
      id: e.auditId,
      at: e.at,
      title: humanizeStatus(e.action.replace(/[.-]/g, '_')),
      actor: `${e.actorKind.toLowerCase()} ${e.actorId}`,
      detail:
        details.before !== undefined || details.after !== undefined ? (
          <JsonDiff before={details.before} after={details.after} />
        ) : Object.keys(e.details).length ? (
          <pre style={{ fontSize: 12 }}>{JSON.stringify(e.details, null, 2)}</pre>
        ) : undefined,
    };
  });
  return <Timeline events={events} emptyText="No audit entries for this lottery yet." />;
}

export function LotteryPlaceholderTab({ what, to }: { what: string; to: string }) {
  const { lottery } = useLottery();
  return (
    <EmptyState
      title={`${what} for ${lottery.slug}`}
      body={`The ${what.toLowerCase()} view is registered by its module agent through src/routes/index.tsx. Until then, use the dedicated console.`}
      action={<Button href={to}>Open {what.toLowerCase()} console</Button>}
    />
  );
}
